import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useApiQuery } from '../../../../../api/hooks';
import useLegendsEditState from '../../../../../hooks/useLegendsEditState';
import { PlusIcon, DownloadIcon } from '../../../model/icons';
import GameHeader from '../GameHeader';
import StatusRow from '../StatusRow';
import PointCard from './PointCard';
import ParticipantRow from './ParticipantRow';
import PointModal from './modals/PointModal';
import SettingsModal from './modals/SettingsModal';
import '../games.css';
import './legends.css';

const LEGEND_STATUSES = [
    { value: 'DEVELOP', label: 'Разработка', tone: 'dev' },
    { value: 'ACTIVE', label: 'Активна', tone: 'active' },
    { value: 'FINISHED', label: 'Завершена', tone: 'done' }
];

const TABS = [
    { key: 'points', label: 'Точки' },
    { key: 'parts', label: 'Участники' }
];

// Финишировавшие — первыми (по месту); остальные — по прогрессу (currentSequenceId), у кого больше пройдено — выше.
// currentSequenceId у финишировавших не отражает прогресс надёжно (может быть 0), поэтому используется только для «в процессе».
function sortParticipants(list) {
    return [...list].sort((a, b) => {
        if (a.finished && !b.finished) return -1;
        if (!a.finished && b.finished) return 1;
        if (a.finished && b.finished) return (a.finishOrder || 999) - (b.finishOrder || 999);
        return (b.currentSequenceId ?? 0) - (a.currentSequenceId ?? 0);
    });
}

// Игра «Легенды»: head-card (инфо + настройки + статус) + табы точки/участники.
export default function LegendsGame({ shift }) {
    const smenaId = shift.id;
    const { data: legends, isLoading, error } = useApiQuery(
        ['legends', 'games', smenaId],
        `/api/legends/admin/legends/find-by-smena?smenaId=${smenaId}`,
        { enabled: !!smenaId, refetchOnMount: 'always' }
    );

    if (error) return <div className="vp-empty">{error.response?.data?.message || 'Ошибка загрузки игры «Легенды»'}</div>;
    if (isLoading) return <div className="vp-empty">Загрузка…</div>;
    if (!legends) return null;

    return <LegendsContent legends={legends} shift={shift} />;
}

LegendsGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};

function LegendsContent({ legends, shift }) {
    const legendsState = useLegendsEditState(legends.id);
    const [tab, setTab] = useState('points');
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [status, setStatus] = useState(legends.status);
    const [points, setPoints] = useState(() => [...(legends.pointSet || [])].sort((a, b) => a.id - b.id));
    const [openPointId, setOpenPointId] = useState(null);
    const [participants, setParticipants] = useState([]);

    const { getParticipants } = legendsState;
    const reloadParticipants = useCallback(async () => {
        const data = await getParticipants();
        setParticipants(sortParticipants(data || []));
    }, [getParticipants]);

    useEffect(() => { reloadParticipants(); }, [reloadParticipants]);

    const handleCreate = async () => {
        try {
            const newPoint = await legendsState.createPoint();
            if (newPoint) setPoints(prev => [...prev, newPoint]);
        } catch (e) { /* ошибку покажет хук */ }
    };
    const handleDeletePoint = (id) => setPoints(prev => prev.filter(p => p.id !== id));
    const handleSavePoint = (updated) => setPoints(prev => prev.map(p => (p.id === updated.id ? updated : p)));
    const handleStatus = async (newStatus) => {
        try {
            await legendsState.updateStatus(newStatus);
            setStatus(newStatus);
        } catch (e) { /* ошибку покажет хук */ }
    };

    const openPoint = openPointId != null ? points.find(p => p.id === openPointId) : null;

    const meta = (
        <>
            <span><b>{points.length}</b> точек</span>
            <span className="dot">·</span>
            <span><b>{participants.length}</b> участников</span>
        </>
    );

    return (
        <>
            <GameHeader
                title={`Легенды — ${shift.title || ''}`}
                meta={meta}
                onSettings={() => setSettingsOpen(true)}
                status={<StatusRow statuses={LEGEND_STATUSES} current={status} onChange={handleStatus} />}
            />
            <section className="content white">
                <div className="bt-tabs">
                    {TABS.map(t => (
                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                        <a key={t.key} className={tab === t.key ? 'on' : ''} onClick={() => setTab(t.key)}>{t.label}</a>
                    ))}
                </div>

                {tab === 'points' && (
                    <div className="bt-panel">
                        <div className="ev-head">
                            <h2>Точки <span className="fr-cnt">({points.length})</span></h2>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button type="button" className="btn btn-ghost" onClick={legendsState.downloadQr}>
                                    <span className="ic"><DownloadIcon /></span>Скачать QR-коды
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleCreate}>
                                    <span className="ic"><PlusIcon /></span>Добавить точку
                                </button>
                            </div>
                        </div>
                        {points.length > 0 ? (
                            <div className="lg-points">
                                {points.map((point, index) => (
                                    <PointCard key={point.id} point={point} index={index} qrBaseUrl={legends.qrBaseUrl} onOpen={setOpenPointId} />
                                ))}
                            </div>
                        ) : <div className="bt-empty">Нет точек</div>}
                    </div>
                )}

                {tab === 'parts' && (
                    <div className="bt-panel">
                        <div className="ev-head">
                            <h2>Участники <span className="fr-cnt">({participants.length})</span></h2>
                        </div>
                        {participants.length > 0 ? (
                            <div>
                                {participants.map((p, index) => (
                                    <ParticipantRow
                                        key={p.id}
                                        participant={p}
                                        index={index}
                                        points={points}
                                        qrBaseUrl={legends.qrBaseUrl}
                                        legendsState={legendsState}
                                        onReload={reloadParticipants}
                                    />
                                ))}
                            </div>
                        ) : <div className="bt-empty">Нет участников</div>}
                    </div>
                )}
            </section>

            {openPoint && (
                <PointModal
                    point={openPoint}
                    legendsState={legendsState}
                    qrBaseUrl={legends.qrBaseUrl}
                    onClose={() => setOpenPointId(null)}
                    onDelete={handleDeletePoint}
                    onSave={handleSavePoint}
                />
            )}
            {settingsOpen && (
                <SettingsModal legends={legends} legendsState={legendsState} onClose={() => setSettingsOpen(false)} />
            )}
        </>
    );
}

LegendsContent.propTypes = {
    legends: PropTypes.object.isRequired,
    shift: PropTypes.shape({ title: PropTypes.string }).isRequired
};
